import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MessageSquare, Bot, Navigation2, LineChart, Shield, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const FloatingAIButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Don't show on auth page or workspace
  if (location.pathname === '/auth' || location.pathname === '/workspace') {
    return null;
  }

  const agents = [
    {
      id: 'loaded-bot',
      name: 'LoadedBot',
      icon: Bot,
      description: 'General career guidance',
      color: 'bg-gradient-to-r from-blue-500 to-cyan-500'
    },
    {
      id: 'career-navigator',
      name: 'Career Navigator', 
      icon: Navigation2,
      description: 'Strategic planning',
      color: 'bg-gradient-to-r from-purple-500 to-pink-500'
    },
    {
      id: 'score-booster',
      name: 'Score Booster',
      icon: LineChart,
      description: 'Skill development',
      color: 'bg-gradient-to-r from-green-500 to-emerald-500'
    },
    {
      id: 'future-proof-advisor',
      name: 'Future Proof',
      icon: Shield,
      description: 'Industry insights',
      color: 'bg-gradient-to-r from-orange-500 to-red-500'
    }
  ];

  const handleAgentSelect = (agentId: string, agentName: string) => {
    navigate('/workspace', { state: { selectedAgent: agentId } });
    setIsOpen(false);
    toast.info(`Switched to ${agentName}`, {
      description: "Ready to help with your career!"
    });
  };

  return (
    <>
      {/* Backdrop with enhanced styling */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 animate-fade-in"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Agent buttons - enhanced radial layout */}
      {isOpen && agents.map((agent, index) => {
        const angle = (index * 90) - 45; // Spread around a quarter circle
        const radius = 130;
        const x = Math.cos((angle * Math.PI) / 180) * radius;
        const y = Math.sin((angle * Math.PI) / 180) * radius;
        
        return (
          <div
            key={agent.id}
            className="fixed z-40 transition-all duration-500 ease-out animate-scale-in"
            style={{
              bottom: `${110 + y}px`,
              right: `${20 - x}px`,
              animationDelay: `${index * 100}ms`,
            }}
          >
            <div className="flex flex-col items-center gap-2">
              <Button
                onClick={() => handleAgentSelect(agent.id, agent.name)}
                className={`w-12 h-12 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-125 ${agent.color} ring-2 ring-white/20 hover:ring-white/40`}
                size="icon"
              >
                <agent.icon className="h-5 w-5 text-white drop-shadow-sm" />
              </Button>
              <span className="text-xs font-semibold text-white bg-gradient-to-r from-black/70 to-black/50 px-3 py-1.5 rounded-full backdrop-blur-md whitespace-nowrap border border-white/10 shadow-lg">
                {agent.name}
              </span>
            </div>
          </div>
        );
      })}

      {/* Enhanced Main FAB */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-20 md:bottom-6 right-4 md:right-6 w-14 h-14 md:w-16 md:h-16 rounded-full bg-gradient-to-r from-primary via-primary to-accent text-white shadow-2xl hover:shadow-primary/25 transition-all duration-300 hover:scale-110 z-40 ring-4 ring-white/10 hover:ring-white/20"
        size="icon"
      >
        <div className={`transition-all duration-300 ${isOpen ? 'rotate-45 scale-110' : 'rotate-0 scale-100'}`}>
          {isOpen ? (
            <X className="h-6 w-6 drop-shadow-sm" />
          ) : (
            <MessageSquare className="h-6 w-6 drop-shadow-sm" />
          )}
        </div>
      </Button>
    </>
  );
};

export default FloatingAIButton;